<script setup lang="ts">
import { ref, reactive, computed } from 'vue'
import { useRouter } from 'vue-router'
import { Mail, ArrowLeft, ArrowRight, Loader2, MailCheck, ShieldCheck, Zap, BarChart3 } from 'lucide-vue-next'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { useToast } from '@/composables/useToast'
import { useAuthStore } from '@/stores/auth'

const router = useRouter()
const authStore = useAuthStore()
const toast = useToast()

const loading = ref(false)
const sent = ref(false)
const touched = reactive({ email: false })

const form = reactive({ email: '' })

const emailError = computed(() => {
  if (!touched.email) return ''
  if (!form.email) return 'El email es obligatorio'
  const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  return re.test(form.email) ? '' : 'Email no válido'
})

const isValid = computed(() => !emailError.value && form.email)

const handleSubmit = async () => {
  touched.email = true
  if (!isValid.value) {
    toast.error('Por favor completa los campos correctamente')
    return
  }
  loading.value = true
  const success = await authStore.forgotPassword(form.email)
  loading.value = false
  if (success) {
    sent.value = true
    toast.success('Instrucciones enviadas')
  }
}
</script>

<template>
  <div class="min-h-screen grid md:grid-cols-2 lg:grid-cols-[5fr_7fr] bg-background">
    <aside
      class="relative hidden md:flex flex-col justify-between p-6 md:p-8 lg:p-12 xl:p-16 text-white overflow-hidden bg-gradient-to-br from-[var(--dodger-blue)] via-[var(--azure-blue)] to-[var(--cobalt-blue)]"
    >
      <!-- Overlay oscuro para garantizar contraste del texto blanco sobre las zonas claras del degradado -->
      <div class="absolute inset-0 bg-gradient-to-br from-black/55 via-black/30 to-black/45" />

      <div class="absolute inset-0 opacity-10 [background-image:radial-gradient(circle_at_20%_20%,white_1px,transparent_1px)] [background-size:32px_32px]" />
      <div class="absolute -top-24 -right-24 h-96 w-96 rounded-full bg-white/5 blur-3xl" />
      <div class="absolute -bottom-32 -left-16 h-96 w-96 rounded-full bg-[var(--neon-pink)]/10 blur-3xl" />

      <div class="relative z-10 flex items-center gap-3">
        <img src="@/assets/combined_logo.png" alt="CTI CRM" class="h-10 md:h-11 lg:h-12 w-auto" />
      </div>

      <div class="relative z-10 space-y-4 md:space-y-6">
        <h1 class="text-2xl md:text-3xl lg:text-4xl xl:text-5xl font-bold leading-tight tracking-tight drop-shadow-[0_1px_4px_rgba(0,0,0,0.45)]">
          Centro de Contacto<br />Inteligente
        </h1>
        <p class="text-base md:text-lg text-white/90 max-w-sm md:max-w-md drop-shadow-[0_1px_3px_rgba(0,0,0,0.4)]">
          Recupera el acceso a tu cuenta de forma segura.
        </p>
        <ul class="space-y-2 md:space-y-3 text-white/95">
          <li class="flex items-center gap-3">
            <span class="grid h-8 w-8 md:h-9 md:w-9 place-items-center rounded-full bg-white/15 shrink-0">
              <Zap class="h-4 w-4" />
            </span>
            <span class="text-sm md:text-base">Dashboard en tiempo real</span>
          </li>
          <li class="flex items-center gap-3">
            <span class="grid h-8 w-8 md:h-9 md:w-9 place-items-center rounded-full bg-white/15 shrink-0">
              <BarChart3 class="h-4 w-4" />
            </span>
            <span class="text-sm md:text-base">Analítica avanzada de conversión</span>
          </li>
          <li class="flex items-center gap-3">
            <span class="grid h-8 w-8 md:h-9 md:w-9 place-items-center rounded-full bg-white/15 shrink-0">
              <ShieldCheck class="h-4 w-4" />
            </span>
            <span class="text-sm md:text-base">Roles y permisos por equipo</span>
          </li>
        </ul>
      </div>

      <p class="relative z-10 text-xs md:text-sm text-white/60">Data Lean Makers, S.L. © {{ new Date().getFullYear() }}</p>
    </aside>

    <main class="flex items-center justify-center p-4 sm:p-6 md:p-10 lg:p-12">
      <div class="w-full max-w-sm space-y-6 md:max-w-md">
        <div class="md:hidden flex justify-center">
          <img src="@/assets/combined_logo.png" alt="CTI CRM" class="h-12 w-auto" />
        </div>

        <header v-if="!sent" class="space-y-1.5">
          <h2 class="text-xl sm:text-2xl font-semibold tracking-tight">Recuperar contraseña</h2>
          <p class="text-sm text-muted-foreground">
            Ingresa tu email y te enviaremos instrucciones para restablecer tu contraseña.
          </p>
        </header>

        <form v-if="!sent" class="space-y-4" @submit.prevent="handleSubmit">
          <div class="space-y-2">
            <Label for="email">Email</Label>
            <div class="relative">
              <Mail class="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                id="email"
                v-model="form.email"
                type="email"
                autocomplete="email"
                name="email"
                placeholder="tu@email.com"
                class="pl-9"
                :aria-invalid="!!emailError"
                :aria-describedby="emailError ? 'email-error' : undefined"
                @blur="touched.email = true"
              />
            </div>
            <p v-if="emailError" id="email-error" class="text-xs text-destructive">{{ emailError }}</p>
          </div>

          <Button type="submit" class="w-full gap-2" :disabled="loading">
            <Loader2 v-if="loading" class="h-4 w-4 animate-spin" />
            <ArrowRight v-else class="h-4 w-4" />
            <span>Enviar instrucciones</span>
          </Button>
        </form>

        <div v-else class="space-y-6 text-center">
          <div class="mx-auto grid h-12 w-12 place-items-center rounded-full bg-primary/10">
            <MailCheck class="h-6 w-6 text-primary" />
          </div>
          <div class="space-y-1.5">
            <h2 class="text-2xl font-semibold tracking-tight">Email enviado</h2>
            <p class="text-sm text-muted-foreground">
              Si el email existe en nuestro sistema, recibirás instrucciones para restablecer tu contraseña.
            </p>
          </div>
          <Button class="w-full gap-2" @click="router.push('/login')">
            <ArrowLeft class="h-4 w-4" />
            <span>Volver al inicio de sesión</span>
          </Button>
        </div>

        <button
          v-if="!sent"
          type="button"
          class="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground underline-offset-4 hover:underline"
          @click="router.push('/login')"
        >
          <ArrowLeft class="h-3.5 w-3.5" />
          Volver al inicio de sesión
        </button>
      </div>
    </main>
  </div>
</template>